import { useEffect, useRef, useState } from "react";
import { ChevronDown, Check } from "lucide-react";
import { useHomeOSState } from "../state/HomeOSStateProvider";
import { firstNameOf } from "../utils/foodIntelligence";

/* ════════════════════════════════════════════════════════════════════════
   Cooking for — a quiet household context control for the Kitchen.
   Empty selection = whole household. Keyboard accessible.
   ════════════════════════════════════════════════════════════════════════ */

const inter = { fontFamily: "Inter, system-ui, sans-serif" };

export function CookingForSelector() {
  const { members, kitchenCookingFor, setKitchenCookingFor } = useHomeOSState();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  if (!members || members.length === 0) return null;

  const validIds = new Set(members.map(m => m.id));
  const selected = (kitchenCookingFor || []).filter(id => validIds.has(id));
  const isWhole = selected.length === 0;

  const label = isWhole
    ? "Whole household"
    : selected.length > 3
      ? `${selected.length} of the household`
      : members.filter(m => selected.includes(m.id)).map(firstNameOf).join(" · ");

  const toggleMember = (id) => {
    const next = selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id];
    setKitchenCookingFor(next.length >= members.length ? [] : next);
  };

  return (
    <div ref={rootRef} className="relative inline-flex items-baseline gap-3" data-testid="cooking-for">
      <span className="text-[11px] tracking-[0.14em] uppercase" style={{ ...inter, color: "var(--ara-ink-soft)", fontWeight: 500 }}>
        Cooking for
      </span>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 text-[13.5px] min-h-[36px] transition-opacity hover:opacity-70"
        style={{ ...inter, color: "var(--ara-ink)", fontWeight: 450, background: "none", border: "none", cursor: "pointer", padding: 0, borderBottom: "1px solid var(--ara-line)" }}
        data-testid="cooking-for-toggle"
      >
        {label}
        <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.5} style={{ color: "var(--ara-ink-soft)", transform: open ? "rotate(180deg)" : "none", transition: "transform 150ms" }} />
      </button>

      {open && (
        <div
          className="absolute left-0 top-full mt-2 z-40 w-56 rounded-[14px] py-2 animate-fadeUp"
          style={{ background: "var(--ara-surface)", border: "1px solid var(--ara-line)", boxShadow: "0 8px 28px rgba(31,31,31,0.08)" }}
          role="group"
          aria-label="Who are you cooking for?"
          data-testid="cooking-for-menu"
        >
          <button
            type="button"
            onClick={() => { setKitchenCookingFor([]); setOpen(false); }}
            className="w-full flex items-center justify-between gap-3 px-4 py-2.5 min-h-[40px] text-left transition-opacity hover:opacity-70"
            style={{ ...inter, fontSize: 13.5, color: "var(--ara-ink)", fontWeight: isWhole ? 500 : 400, background: "none", border: "none", cursor: "pointer" }}
            aria-pressed={isWhole}
            data-testid="cooking-for-whole-household"
          >
            Whole household
            {isWhole && <Check className="w-3.5 h-3.5" strokeWidth={1.5} style={{ color: "var(--ara-ink-soft)" }} />}
          </button>
          <div className="my-1 mx-4" style={{ borderTop: "1px solid var(--ara-line-soft)" }} />
          {members.map(m => {
            const active = selected.includes(m.id);
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => toggleMember(m.id)}
                className="w-full flex items-center justify-between gap-3 px-4 py-2.5 min-h-[40px] text-left transition-opacity hover:opacity-70"
                style={{ ...inter, fontSize: 13.5, color: "var(--ara-ink)", fontWeight: active ? 500 : 400, background: "none", border: "none", cursor: "pointer" }}
                aria-pressed={active}
                data-testid={`cooking-for-member-${m.id}`}
              >
                {firstNameOf(m)}
                {active && <Check className="w-3.5 h-3.5" strokeWidth={1.5} style={{ color: "var(--ara-ink-soft)" }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
